import React, { useState, useEffect } from 'react';
import {
  getBuildings, getUnits, getRentStatus,
  getPendingPayments, approvePayment, rejectPayment,
  createBuilding, createUnit, createTenant,
  updateUnit, updateTenant, deleteTenant, deleteUnit,
  sendReminder, tenantMoveOut,
  getTenantPaymentHistory, recordManualPayment,
  getBuildingArrears, getTenantArrears, remindAllArrears, getTenants,
  getDepositPayments, recordDepositPayment, deleteDepositPayment
} from '../services/api';

const inputStyle = {
  width: '100%', padding: '10px 12px',
  border: '1.5px solid #e2e8f0', borderRadius: 7,
  fontSize: 14, boxSizing: 'border-box',
  fontFamily: 'inherit'
};

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{
        display: 'block', marginBottom: 5,
        fontSize: 13, fontWeight: 500, color: '#374151'
      }}>{label}</label>
      {children}
    </div>
  );
}

function Modal({ title, onClose, onSave, saveLabel, formError, formLoading, children }) {
  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center',
      justifyContent: 'center', zIndex: 1000, padding: 20
    }}>
      <div style={{
        background: 'white', borderRadius: 12,
        padding: 28, width: '100%', maxWidth: 440,
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        maxHeight: '90vh', overflowY: 'auto'
      }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', marginBottom: 24
        }}>
          <h3 style={{ fontSize: 17, fontWeight: 600 }}>{title}</h3>
          <button onClick={onClose} style={{
            background: 'none', border: 'none',
            fontSize: 20, cursor: 'pointer', color: '#94a3b8'
          }}>✕</button>
        </div>
        {children}
        {formError && (
          <p style={{ color: '#dc2626', fontSize: 13, marginTop: 12 }}>
            {formError}
          </p>
        )}
        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button onClick={onClose} style={{
            flex: 1, padding: 11, background: '#f1f5f9',
            border: 'none', borderRadius: 8, fontSize: 14,
            cursor: 'pointer', color: '#64748b'
          }}>Cancel</button>
          <button onClick={onSave} disabled={formLoading} style={{
            flex: 2, padding: 11,
            background: formLoading ? '#93c5fd' : '#1a56db',
            color: 'white', border: 'none', borderRadius: 8,
            fontSize: 14, fontWeight: 600, cursor: 'pointer'
          }}>
            {formLoading ? 'Saving...' : saveLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard({ user, onLogout }) {
  const [buildings, setBuildings] = useState([]);
  const [rentStatus, setRentStatus] = useState(null);
  const [pending, setPending] = useState([]);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [units, setUnits] = useState([]);

  // Referral code — passed in via user prop immediately on login/register
  const [referralCode] = useState(user.referral_code || '');
  const [referralCopied, setReferralCopied] = useState(false);

  const [showAddBuilding, setShowAddBuilding] = useState(false);
  const [showAddUnit, setShowAddUnit] = useState(false);
  const [showAddTenant, setShowAddTenant] = useState(false);
  const [showEditTenant, setShowEditTenant] = useState(false);
  const [showDeleteTenant, setShowDeleteTenant] = useState(false);
  const [showDeleteUnit, setShowDeleteUnit] = useState(false);
  const [showEditUnit, setShowEditUnit] = useState(false);
  const [showManualPayment, setShowManualPayment] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showMoveOut, setShowMoveOut] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [selectedTenantForDeposit, setSelectedTenantForDeposit] = useState(null);
  const [depositHistory, setDepositHistory] = useState(null);
  const [depositInstalment, setDepositInstalment] = useState('');
  const [depositDate, setDepositDate] = useState('');
  const [depositMode, setDepositMode] = useState('Cash');
  const [depositRef, setDepositRef] = useState('');
  const [depositNote, setDepositNote] = useState('');
  // Pro-rata state
  const [prorataDismissed, setProrataDismissed] = useState({}); // { tenantId: true }
  const [showProrataModal, setShowProrataModal] = useState(false);
  const [prorataData, setProrataData] = useState(null); // { tenant, unit, days, amount, month }
  const [depositLoading, setDepositLoading] = useState(false);
  const [depositError, setDepositError] = useState('');

  const [selectedUnitForTenant, setSelectedUnitForTenant] = useState(null);
  const [selectedUnitForEdit, setSelectedUnitForEdit] = useState(null);
  const [selectedUnitForDelete, setSelectedUnitForDelete] = useState(null);
  const [selectedTenantForEdit, setSelectedTenantForEdit] = useState(null);
  const [selectedTenantForDelete, setSelectedTenantForDelete] = useState(null);
  const [selectedTenantForPayment, setSelectedTenantForPayment] = useState(null);
  const [selectedTenantForHistory, setSelectedTenantForHistory] = useState(null);
  const [selectedTenantForMoveOut, setSelectedTenantForMoveOut] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);

  const [buildingName, setBuildingName] = useState('');
  const [buildingAddress, setBuildingAddress] = useState('');
  const [unitNumber, setUnitNumber] = useState('');
  const [unitFloor, setUnitFloor] = useState('');
  const [unitRent, setUnitRent] = useState('');
  const [unitType, setUnitType] = useState('rental');
  const [leaseAmount, setLeaseAmount] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [tenantPhone, setTenantPhone] = useState('');
  const [tenantEmail, setTenantEmail] = useState('');       // NEW
  const [tenantAadhaar, setTenantAadhaar] = useState('');   // NEW
  const [tenantDeposit, setTenantDeposit] = useState('');
  const [tenantMoveIn, setTenantMoveIn] = useState('');
  const [manualAmount, setManualAmount] = useState('');
  const [manualDate, setManualDate] = useState('');
  const [manualMode, setManualMode] = useState('Cash');
  const [manualRef, setManualRef] = useState('');
  const [manualMonth, setManualMonth] = useState('');
  const [moveOutDepositRefunded, setMoveOutDepositRefunded] = useState(false);
  const [moveOutRefundAmount, setMoveOutRefundAmount] = useState('');

  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const [recentMoveouts, setRecentMoveouts] = useState([]);
  const [arrearsData, setArrearsData] = useState(null);
  const [arrearsLoading, setArrearsLoading] = useState(false);
  const [showTenantArrears, setShowTenantArrears] = useState(false);
  const [, setSelectedTenantArrears] = useState(null);
  const [tenantArrearsDetail, setTenantArrearsDetail] = useState(null);

  const now = new Date();
  const currentMonth = now.toLocaleString('default', { month: 'long' }) + '-' + now.getFullYear();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, []);

  async function loadData() {
    setLoading(true);
    try {
      const [bRes, pRes] = await Promise.all([getBuildings(), getPendingPayments()]);
      setBuildings(bRes.data);
      setPending(pRes.data);
      if (bRes.data.length > 0) {
        const first = bRes.data[0];
        setSelectedBuilding(first);
        const [sRes, uRes, aRes, tRes] = await Promise.all([
          getRentStatus(first.id, currentMonth),
          getUnits(first.id),
          getBuildingArrears(first.id),
          getTenants()
        ]);
        setRentStatus(sRes.data);
        setUnits(uRes.data);
        setArrearsData(aRes.data);
        setRecentMoveouts(tRes.data.recent_moveouts || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function loadBuilding(building) {
    setSelectedBuilding(building);
    try {
      const [sRes, uRes, aRes, tRes] = await Promise.all([
        getRentStatus(building.id, currentMonth),
        getUnits(building.id),
        getBuildingArrears(building.id),
        getTenants()
      ]);
      setRentStatus(sRes.data);
      setUnits(uRes.data);
      setArrearsData(aRes.data);
      setRecentMoveouts(tRes.data.recent_moveouts || []);
    } catch (err) { console.error(err); }
  }

  function resetForms() {
    setBuildingName(''); setBuildingAddress('');
    setUnitNumber(''); setUnitFloor(''); setUnitRent('');
    setUnitType('rental'); setLeaseAmount('');
    setTenantName(''); setTenantPhone('');
    setTenantEmail(''); setTenantAadhaar('');   // NEW
    setTenantDeposit(''); setTenantMoveIn('');
    setManualAmount(''); setManualDate('');
    setManualMode('Cash'); setManualRef('');
    setManualMonth(currentMonth);
    setMoveOutDepositRefunded(false);
    setMoveOutRefundAmount('');
    setDepositInstalment('');
    setDepositDate('');
    setDepositMode('Cash');
    setDepositRef('');
    setDepositNote('');
    setDepositError('');
    setFormError(''); setFormLoading(false);
  }

  function showSuccess(msg) {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 4000);
  }

  // Safely extract a string from FastAPI error responses.
  // detail can be a string OR an array of Pydantic validation objects.
  function parseApiError(err, fallback) {
    const detail = err.response?.data?.detail;
    if (!detail) return fallback;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail.map(d => d.msg || JSON.stringify(d)).join(', ');
    }
    if (typeof detail === 'object') return detail.msg || JSON.stringify(detail);
    return String(detail);
  }

  function handleCopyReferral() {
    const link = `https://rentos.co.in/register?ref=${referralCode}`;
    navigator.clipboard.writeText(link).then(() => {
      setReferralCopied(true);
      setTimeout(() => setReferralCopied(false), 2500);
    });
  }

  async function handleAddBuilding() {
    if (!buildingName.trim()) { setFormError('Enter building name'); return; }
    if (!buildingAddress.trim()) { setFormError('Enter address'); return; }
    setFormLoading(true); setFormError('');
    try {
      await createBuilding({
        name: buildingName.trim(),
        address: buildingAddress.trim(),
        landlord_id: user.id
      });
      setShowAddBuilding(false); resetForms();
      await loadData();
      showSuccess('Building added successfully');
    } catch (err) {
      setFormError(parseApiError(err, 'Failed to save'));
    } finally { setFormLoading(false); }
  }

  async function handleAddUnit() {
    if (!unitNumber.trim()) { setFormError('Enter unit number'); return; }
    if (!unitRent || isNaN(unitRent)) { setFormError('Enter valid monthly amount'); return; }
    setFormLoading(true); setFormError('');
    try {
      await createUnit({
        unit_number: unitNumber.trim(),
        floor: unitFloor ? parseInt(unitFloor) : null,
        rent_amount: parseFloat(unitRent),
        building_id: selectedBuilding.id,
        unit_type: unitType,
        lease_amount: unitType === 'lease' && leaseAmount ? parseFloat(leaseAmount) : null
      });
      setShowAddUnit(false); resetForms();
      await loadBuilding(selectedBuilding);
      showSuccess('Unit added successfully');
    } catch (err) {
      setFormError(parseApiError(err, 'Failed to save'));
    } finally { setFormLoading(false); }
  }

  async function handleEditUnit() {
    if (!unitNumber.trim()) { setFormError('Enter unit number'); return; }
    if (!unitRent || isNaN(unitRent)) { setFormError('Enter valid monthly amount'); return; }
    setFormLoading(true); setFormError('');
    try {
      await updateUnit(selectedUnitForEdit.id, {
        unit_number: unitNumber.trim(),
        floor: unitFloor ? parseInt(unitFloor) : null,
        rent_amount: parseFloat(unitRent),
        building_id: selectedBuilding.id,
        unit_type: unitType,
        lease_amount: unitType === 'lease' && leaseAmount ? parseFloat(leaseAmount) : null
      });
      setShowEditUnit(false); resetForms();
      await loadBuilding(selectedBuilding);
      showSuccess('Unit updated. Tenant notified on WhatsApp if rent changed.');
    } catch (err) {
      setFormError(parseApiError(err, 'Failed to update'));
    } finally { setFormLoading(false); }
  }

  async function handleAddTenant() {
    if (!tenantName.trim()) { setFormError('Enter tenant name'); return; }
    if (tenantPhone.length !== 10) { setFormError('Enter valid 10-digit phone'); return; }
    if (tenantAadhaar && tenantAadhaar.length !== 4) { setFormError('Aadhaar must be last 4 digits'); return; }
    setFormLoading(true); setFormError('');
    try {
      await createTenant({
        name: tenantName.trim(),
        phone: tenantPhone,
        email: tenantEmail.trim() || null,
        aadhaar_last4: tenantAadhaar || null,
        unit_id: selectedUnitForTenant.id,
        deposit_amount: tenantDeposit ? parseFloat(tenantDeposit) : null,
        move_in_date: tenantMoveIn || null
      });
      setShowAddTenant(false); resetForms();
      await loadBuilding(selectedBuilding);
      showSuccess('Tenant added. Welcome message sent on WhatsApp.');
    } catch (err) {
      setFormError(parseApiError(err, 'Failed to save'));
    } finally { setFormLoading(false); }
  }

  async function handleEditTenant() {
    if (!tenantName.trim()) { setFormError('Enter tenant name'); return; }
    if (tenantPhone.length !== 10) { setFormError('Enter valid 10-digit phone'); return; }
    if (tenantAadhaar && tenantAadhaar.length !== 4) { setFormError('Aadhaar must be last 4 digits'); return; }
    setFormLoading(true); setFormError('');
    try {
      await updateTenant(selectedTenantForEdit.id, {
        name: tenantName.trim(),
        phone: tenantPhone,
        email: tenantEmail.trim() || null,
        aadhaar_last4: tenantAadhaar || null,
        unit_id: selectedTenantForEdit.unit_id,  // injected when Edit modal opens
        deposit_amount: tenantDeposit ? parseFloat(tenantDeposit) : null,
        move_in_date: tenantMoveIn || null
      });
      setShowEditTenant(false); resetForms();
      await loadBuilding(selectedBuilding);
      showSuccess('Tenant details updated successfully.');
    } catch (err) {
      setFormError(parseApiError(err, 'Failed to update'));
    } finally { setFormLoading(false); }
  }

  async function handleDeleteTenant() {
    setFormLoading(true); setFormError('');
    try {
      await deleteTenant(selectedTenantForDelete.id);
      setShowDeleteTenant(false); resetForms();
      await loadBuilding(selectedBuilding);
      showSuccess(selectedTenantForDelete.name + ' deleted successfully.');
    } catch (err) {
      setFormError(parseApiError(err, 'Failed to delete'));
    } finally { setFormLoading(false); }
  }

  async function handleDeleteUnit() {
    setFormLoading(true); setFormError('');
    try {
      await deleteUnit(selectedUnitForDelete.id);
      setShowDeleteUnit(false); resetForms();
      await loadBuilding(selectedBuilding);
      showSuccess('Unit ' + selectedUnitForDelete.unit_number + ' deleted successfully.');
    } catch (err) {
      setFormError(parseApiError(err, 'Failed to delete'));
    } finally { setFormLoading(false); }
  }

  async function handleManualPayment() {
    if (!manualAmount || isNaN(manualAmount)) { setFormError('Enter valid amount'); return; }
    if (!manualDate) { setFormError('Select payment date'); return; }
    if (!manualMonth) { setFormError('Enter month'); return; }
    setFormLoading(true); setFormError('');
    try {
      await recordManualPayment({
        tenant_id: selectedTenantForPayment.id,
        amount: parseFloat(manualAmount),
        payment_date: manualDate,
        month: manualMonth,
        payment_mode: manualMode,
        upi_transaction_id: manualRef || null
      });
      setShowManualPayment(false); resetForms();
      await loadBuilding(selectedBuilding);
      showSuccess('Payment recorded. Receipt sent to tenant on WhatsApp.');
    } catch (err) {
      setFormError(parseApiError(err, 'Failed to record'));
    } finally { setFormLoading(false); }
  }

  async function handleSendReminder(tenant) {
    setActionLoading('remind-' + tenant.id);
    try {
      await sendReminder(tenant.id);
      showSuccess('Reminder sent to ' + tenant.name + ' on WhatsApp');
    } catch (err) {
      alert(parseApiError(err, 'Failed to send reminder'));
    } finally { setActionLoading(null); }
  }

  async function handleMoveOut() {
    setFormLoading(true); setFormError('');
    try {
      const res = await tenantMoveOut(selectedTenantForMoveOut.id, {
        deposit_refunded: moveOutDepositRefunded,
        refund_amount: moveOutRefundAmount ? parseFloat(moveOutRefundAmount) : null
      });
      setShowMoveOut(false); resetForms();
      await loadBuilding(selectedBuilding);
      showSuccess(
        selectedTenantForMoveOut.name + ' moved out. ' +
        (res.data.deposit_refunded
          ? 'Deposit Rs.' + res.data.refund_amount.toLocaleString() + ' marked refunded.'
          : 'Deposit Rs.' + res.data.deposit_amount.toLocaleString() + ' pending refund.')
      );
    } catch (err) {
      setFormError(parseApiError(err, 'Failed'));
    } finally { setFormLoading(false); }
  }

  async function handleViewHistory(tenant) {
    setSelectedTenantForHistory(tenant);
    try {
      const res = await getTenantPaymentHistory(tenant.id);
      setPaymentHistory(res.data);
      setShowHistory(true);
    } catch (err) {
      alert('Failed to load history');
    }
  }

  async function handleOpenDepositModal(tenant) {
    setSelectedTenantForDeposit(tenant);
    setDepositError('');
    setDepositInstalment('');
    setDepositDate(new Date().toISOString().split('T')[0]);
    setDepositMode('Cash');
    setDepositRef('');
    setDepositNote('');
    try {
      const res = await getDepositPayments(tenant.id);
      setDepositHistory(res.data);
    } catch (err) {
      setDepositHistory(null);
    }
    setShowDepositModal(true);
  }

  async function handleRecordDeposit() {
    if (!depositInstalment || isNaN(depositInstalment) || parseFloat(depositInstalment) <= 0) {
      setDepositError('Enter a valid amount');
      return;
    }
    if (!depositDate) { setDepositError('Select a payment date'); return; }
    setDepositLoading(true); setDepositError('');
    try {
      const res = await recordDepositPayment(selectedTenantForDeposit.id, {
        amount_paid: parseFloat(depositInstalment),
        payment_date: depositDate,
        payment_mode: depositMode,
        reference: depositRef || null,
        note: depositNote || null
      });
      // Refresh deposit history in modal
      const updated = await getDepositPayments(selectedTenantForDeposit.id);
      setDepositHistory(updated.data);
      setDepositInstalment('');
      setDepositDate(new Date().toISOString().split('T')[0]);
      setDepositRef('');
      setDepositNote('');
      showSuccess(
        res.data.fully_paid
          ? selectedTenantForDeposit.name + ' — deposit fully collected!'
          : 'Deposit instalment recorded. Balance: ₹' + res.data.balance_remaining.toLocaleString()
      );
    } catch (err) {
      setDepositError(parseApiError(err, 'Failed to record'));
    } finally {
      setDepositLoading(false);
    }
  }

  async function handleDeleteDepositPayment(dpId) {
    if (!window.confirm('Remove this deposit instalment?')) return;
    try {
      await deleteDepositPayment(dpId);
      const updated = await getDepositPayments(selectedTenantForDeposit.id);
      setDepositHistory(updated.data);
      showSuccess('Deposit entry removed.');
    } catch (err) {
      alert(parseApiError(err, 'Failed to remove'));
    }
  }

  // ── Pro-rata helpers ────────────────────────────────────────
  function calcProrata(moveInDate, rentAmount) {
    const d = new Date(moveInDate);
    const year = d.getFullYear();
    const month = d.getMonth(); // 0-indexed
    const day = d.getDate();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysRemaining = daysInMonth - day + 1;
    const amount = Math.round((rentAmount / daysInMonth) * daysRemaining);
    const monthNames = ['January','February','March','April','May','June',
                        'July','August','September','October','November','December'];
    const monthStr = monthNames[month] + '-' + year;
    return { days: daysRemaining, daysInMonth, amount, month: monthStr, year, monthNum: month };
  }

  function isMidMonthMoveIn(moveInDate) {
    if (!moveInDate) return false;
    const d = new Date(moveInDate);
    return d.getDate() > 1;
  }

  function handleOpenProrataModal(tenant, unit) {
    const pr = calcProrata(tenant.move_in_date, unit.rent_amount);
    setProrataData({ tenant, unit, ...pr });
    setManualAmount(String(pr.amount));
    setManualMonth(pr.month);
    setManualDate(tenant.move_in_date);
    setManualMode('Cash');
    setManualRef('');
    setSelectedTenantForPayment(tenant);
    setShowProrataModal(true);
    setFormError('');
  }

  async function handleSaveProrata() {
    if (!manualAmount || isNaN(manualAmount)) { setFormError('Enter valid amount'); return; }
    if (!manualDate) { setFormError('Select payment date'); return; }
    setFormLoading(true); setFormError('');
    try {
      await recordManualPayment({
        tenant_id: selectedTenantForPayment.id,
        amount: parseFloat(manualAmount),
        payment_date: manualDate,
        month: manualMonth,
        payment_mode: manualMode,
        upi_transaction_id: manualRef || null
      });
      setShowProrataModal(false);
      setProrataDismissed(prev => ({ ...prev, [selectedTenantForPayment.id]: true }));
      resetForms();
      await loadBuilding(selectedBuilding);
      showSuccess('Pro-rata rent recorded. Receipt sent to ' + prorataData.tenant.name + ' on WhatsApp.');
    } catch (err) {
      setFormError(parseApiError(err, 'Failed to record'));
    } finally { setFormLoading(false); }
  }

  async function handleApprove(paymentId) {
    setActionLoading(paymentId);
    try {
      await approvePayment(paymentId);
      await loadData();
    } catch (err) {
      alert(parseApiError(err, 'Error'));
    } finally { setActionLoading(null); }
  }

  async function handleReject(paymentId) {
    const reason = window.prompt(
      'Reason for rejection (required — tenant will be notified):\n\nExamples: "Screenshot is unclear", "Amount does not match", "Transaction ID not found"'
    );
    if (reason === null) return; // User cancelled
    if (!reason.trim()) {
      alert('Please provide a reason for rejection.');
      return;
    }
    setActionLoading(paymentId);
    try {
      await rejectPayment(paymentId, reason.trim());
      await loadData();
    } catch (err) {
      alert(parseApiError(err, 'Error'));
    } finally {
      setActionLoading(null);
    }
  }

  async function handleRemindAllArrears() {
    if (!window.confirm(
      'Send arrears reminders to all tenants with outstanding dues?'
    )) return;
    setArrearsLoading(true);
    try {
      const res = await remindAllArrears(selectedBuilding.id);
      showSuccess(res.data.message);
      await loadBuilding(selectedBuilding);
    } catch (err) {
      alert('Failed to send reminders');
    } finally {
      setArrearsLoading(false); }
  }

  async function handleViewTenantArrears(tenant) {
    setSelectedTenantArrears(tenant);
    try {
      const res = await getTenantArrears(tenant.tenant_id || tenant.id);
      setTenantArrearsDetail(res.data);
      setShowTenantArrears(true);
    } catch (err) {
      alert('Failed to load arrears');
    }
  }

  function refLabel() {
    if (manualMode === 'UPI') return 'UPI Transaction ID';
    if (manualMode === 'Bank Transfer') return 'Bank Reference / UTR Number';
    if (manualMode === 'Cheque') return 'Cheque Number';
    return null;
  }

  function refPlaceholder() {
    if (manualMode === 'UPI') return 'e.g. 606711011862';
    if (manualMode === 'Bank Transfer') return 'e.g. UTR123456789012';
    if (manualMode === 'Cheque') return 'e.g. 000123';
    return '';
  }

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex',
        alignItems: 'center', justifyContent: 'center', color: '#64748b'
      }}>Loading...</div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>

      <div style={{
        background: '#1a56db', color: 'white',
        padding: '0 24px', height: 56,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between'
      }}>
        <span style={{ fontSize: 20, fontWeight: 700 }}>RentOS</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span style={{ fontSize: 13, opacity: 0.9 }}>{user.name}</span>
          <button onClick={onLogout} style={{
            background: 'rgba(255,255,255,0.15)', color: 'white',
            padding: '6px 14px', borderRadius: 6, fontSize: 13,
            border: 'none', cursor: 'pointer'
          }}>Logout</button>
        </div>
      </div>

      {successMsg && (
        <div style={{
          position: 'fixed', top: 16, right: 16, zIndex: 2000,
          background: '#166534', color: 'white',
          padding: '12px 20px', borderRadius: 8,
          fontSize: 13, fontWeight: 500,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
        }}>
          ✓ {successMsg}
        </div>
      )}

      {/* REFERRAL BAR — shown when referral code is available */}
      {referralCode && (
        <div style={{
          background: '#1e3a8a',
          borderBottom: '1px solid #1e3a6a',
          padding: '8px 24px',
          display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', flexWrap: 'wrap', gap: 8
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)' }}>
              Your Referral Code:
            </span>
            <span style={{
              background: 'rgba(255,255,255,0.12)',
              color: 'white', fontWeight: 700,
              fontSize: 13, letterSpacing: 2,
              padding: '2px 10px', borderRadius: 5,
              fontFamily: 'monospace'
            }}>
              {referralCode}
            </span>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>
              · Invite landlords to RentOS
            </span>
          </div>
          <button
            onClick={handleCopyReferral}
            style={{
              background: referralCopied
                ? 'rgba(34,197,94,0.2)'
                : 'rgba(255,255,255,0.12)',
              color: referralCopied ? '#86efac' : 'white',
              border: referralCopied
                ? '1px solid rgba(134,239,172,0.4)'
                : '1px solid rgba(255,255,255,0.25)',
              padding: '4px 14px', borderRadius: 6,
              fontSize: 12, fontWeight: 600, cursor: 'pointer'
            }}
          >
            {referralCopied ? '✓ Link Copied!' : 'Copy Invite Link'}
          </button>
        </div>
      )}

      <div style={{ padding: 24, maxWidth: 1000, margin: '0 auto' }}>

        <div style={{
          display: 'flex', alignItems: 'center',
          gap: 12, marginBottom: 20, flexWrap: 'wrap'
        }}>
          {buildings.map(b => (
            <button key={b.id} onClick={() => loadBuilding(b)} style={{
              padding: '8px 16px', borderRadius: 8,
              border: selectedBuilding?.id === b.id ? 'none' : '1px solid #e2e8f0',
              background: selectedBuilding?.id === b.id ? '#1a56db' : 'white',
              color: selectedBuilding?.id === b.id ? 'white' : '#374151',
              fontSize: 14, fontWeight: 500, cursor: 'pointer'
            }}>{b.name}</button>
          ))}
          <button onClick={() => { resetForms(); setShowAddBuilding(true); }} style={{
            padding: '8px 16px', borderRadius: 8,
            border: '1.5px dashed #93c5fd', background: 'transparent',
            color: '#1a56db', fontSize: 14, cursor: 'pointer'
          }}>+ Add Building</button>
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
          {['overview', 'pending', 'manage', 'arrears'].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{
              padding: '8px 20px', borderRadius: 8,
              border: activeTab === tab ? 'none' : '1px solid #e2e8f0',
              background: activeTab === tab ? '#1a56db' : 'white',
              color: activeTab === tab ? 'white' : '#64748b',
              fontWeight: activeTab === tab ? 600 : 400,
              fontSize: 14, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 8
            }}>
              {tab === 'pending' ? 'Pending Approvals' : 
               tab === 'arrears' ? 'Arrears' :
               tab.charAt(0).toUpperCase() + tab.slice(1)}
              {tab === 'pending' && pending.length > 0 && (
                <span style={{
                  background: '#ef4444', color: 'white',
                  borderRadius: '50%', width: 20, height: 20,
                  display: 'inline-flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: 11, fontWeight: 700
                }}>{pending.length}</span>
              )}
              {tab === 'arrears' && arrearsData && arrearsData.tenants_with_arrears > 0 && (
                <span style={{
                  background: '#f59e0b', color: 'white',
                  borderRadius: '50%', width: 20, height: 20,
                  display: 'inline-flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: 11, fontWeight: 700
                }}>{arrearsData.tenants_with_arrears}</span>
              )}
            </button>
          ))}
        </div>

        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && rentStatus && (
          <div>
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 16, marginBottom: 24
            }}>
              {[
                { label: 'Total Units', value: rentStatus.total_units, color: '#1e293b' },
                {
                  label: 'Paid — ' + rentStatus.month,
                  value: rentStatus.paid_count,
                  color: '#16a34a', border: '#22c55e'
                },
                rentStatus.is_grace_period
                  ? {
                      label: 'Unpaid — ' + rentStatus.month,
                      value: rentStatus.unpaid_count,
                      color: '#dc2626', border: '#ef4444'
                    }
                  : {
                      label: 'Unpaid — ' + rentStatus.month,
                      value: rentStatus.unpaid_count,
                      color: '#dc2626', border: '#ef4444'
                    }
              ].map((s, i) => (
                <div key={i} style={{
                  background: 'white', borderRadius: 12,
                  border: '1px solid #e2e8f0',
                  borderTop: s.border ? '3px solid ' + s.border : undefined,
                  padding: '20px 24px'
                }}>
                  <p style={{ color: '#64748b', fontSize: 12, marginBottom: 6 }}>{s.label}</p>
                  <p style={{ fontSize: 28, fontWeight: 700, color: s.color }}>{s.value}</p>
                </div>
              ))}
            </div>

            <div style={{
              background: 'white', borderRadius: 12,
              border: '1px solid #e2e8f0', padding: 20
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <p style={{ fontSize: 16, fontWeight: 600 }}>
                  {selectedBuilding?.name} — {rentStatus.month}
                </p>
                {rentStatus.is_grace_period && rentStatus.upcoming_month && (
                  <span style={{
                    background: '#dbeafe', color: '#1e40af',
                    fontSize: 12, fontWeight: 500,
                    padding: '4px 12px', borderRadius: 20,
                    border: '1px solid #bfdbfe'
                  }}>
                    {rentStatus.upcoming_month} collection upcoming
                  </span>
                )}
              </div>

              {rentStatus.paid.length > 0 && (
                <div>
                  <p style={{ color: '#16a34a', fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
                    PAID ({rentStatus.paid_count})
                  </p>
                  {rentStatus.paid.map(function(t) {
                    return (
                      <div key={t.tenant_id} style={{
                        display: 'flex', alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '12px 0', borderBottom: '1px solid #f1f5f9'
                      }}>
                        <div>
                          <p style={{ fontWeight: 500, marginBottom: 2 }}>{t.tenant_name}</p>
                          <p style={{ fontSize: 12, color: '#94a3b8' }}>
                            {'Unit ' + t.unit_number + ' · ₹' + t.rent_amount.toLocaleString() + ' · ' + (t.unit_type === 'lease' ? 'Maintenance' : 'Rent') + ' · Paid on ' + t.paid_on}
                          </p>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{
                            background: '#dcfce7', color: '#166534',
                            padding: '3px 10px', borderRadius: 20,
                            fontSize: 12, fontWeight: 500
                          }}>PAID</span>
                          <a href={process.env.REACT_APP_API_URL + '/receipt/' + t.payment_id} target="_blank" rel="noreferrer" style={{ color: '#1a56db', fontSize: 12 }}>Receipt</a>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {rentStatus.unpaid.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <p style={{ color: '#dc2626', fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
                    UNPAID ({rentStatus.unpaid_count})
                  </p>
                  {rentStatus.unpaid.map(function(t) {
                    return (
                      <div key={t.tenant_id} style={{
                        display: 'flex', alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '12px 0', borderBottom: '1px solid #f1f5f9'
                      }}>
                        <div>
                          <p style={{ fontWeight: 500, marginBottom: 2 }}>{t.tenant_name}</p>
                          <p style={{ fontSize: 12, color: '#94a3b8' }}>
                            {'Unit ' + t.unit_number + ' · ₹' + t.rent_amount.toLocaleString() + ' · ' + (t.unit_type === 'lease' ? 'Maintenance' : 'Rent') + ' · ' + rentStatus.month + ' due'}
                          </p>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{
                            background: '#fee2e2', color: '#991b1b',
                            padding: '3px 10px', borderRadius: 20,
                            fontSize: 12, fontWeight: 500
                          }}>UNPAID</span>
                          <button
                            onClick={() => handleSendReminder({ id: t.tenant_id, name: t.tenant_name })}
                            disabled={actionLoading === 'remind-' + t.tenant_id}
                            style={{
                              background: '#fef9c3', color: '#854d0e',
                              padding: '4px 10px', borderRadius: 6,
                              fontSize: 12, border: '1px solid #fde68a',
                              cursor: 'pointer'
                            }}>
                            {actionLoading === 'remind-' + t.tenant_id ? '...' : 'Remind'}
                          </button>
                          <button
                            onClick={() => {
                              setSelectedTenantForPayment({ id: t.tenant_id, name: t.tenant_name });
                              setManualMonth(rentStatus.month);
                              setManualAmount(String(t.rent_amount));
                              setManualDate(new Date().toISOString().split('T')[0]);
                              setFormError('');
                              setShowManualPayment(true);
                            }}
                            style={{
                              background: '#dbeafe', color: '#1e40af',
                              padding: '4px 10px', borderRadius: 6,
                              fontSize: 12, border: '1px solid #bfdbfe',
                              cursor: 'pointer'
                            }}>
                            Record
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* UPCOMING — current month during grace period */}
              {rentStatus.is_grace_period && rentStatus.upcoming && rentStatus.upcoming.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <p style={{ color: '#1e40af', fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
                    UPCOMING — {rentStatus.upcoming_month} ({rentStatus.upcoming_count})
                  </p>
                  {rentStatus.upcoming.map(function(t) {
                    return (
                      <div key={'up-' + t.tenant_id} style={{
                        display: 'flex', alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '12px 0', borderBottom: '1px solid #f1f5f9'
                      }}>
                        <div>
                          <p style={{ fontWeight: 500, marginBottom: 2 }}>{t.tenant_name}</p>
                          <p style={{ fontSize: 12, color: '#94a3b8' }}>
                            {'Unit ' + t.unit_number + ' · ₹' + t.rent_amount.toLocaleString() + ' · ' + (t.unit_type === 'lease' ? 'Maintenance' : 'Rent') + ' · due by 10th'}
                          </p>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{
                            background: '#dbeafe', color: '#1e40af',
                            padding: '3px 10px', borderRadius: 20,
                            fontSize: 12, fontWeight: 500
                          }}>UPCOMING</span>
                          <button
                            onClick={() => {
                              setSelectedTenantForPayment({ id: t.tenant_id, name: t.tenant_name });
                              setManualMonth(rentStatus.upcoming_month);
                              setManualAmount(String(t.rent_amount));
                              setManualDate(new Date().toISOString().split('T')[0]);
                              setFormError('');
                              setShowManualPayment(true);
                            }}
                            style={{
                              background: '#dbeafe', color: '#1e40af',
                              padding: '4px 10px', borderRadius: 6,
                              fontSize: 12, border: '1px solid #bfdbfe',
                              cursor: 'pointer'
                            }}>
                            Record
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'overview' && !rentStatus && (
          <div style={{
            background: 'white', borderRadius: 12,
            border: '1px solid #e2e8f0', padding: 40, textAlign: 'center'
          }}>
            <p style={{ color: '#64748b' }}>No buildings found.</p>
            <button onClick={() => { resetForms(); setShowAddBuilding(true); }} style={{
              marginTop: 12, background: '#1a56db', color: 'white',
              padding: '8px 20px', borderRadius: 8, border: 'none',
              fontSize: 14, cursor: 'pointer'
            }}>Add Your First Building</button>
          </div>
        )}

        {/* PENDING TAB */}
        {activeTab === 'pending' && (
          <div>
            <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>
              Payments Awaiting Approval
            </p>
            {pending.length === 0 ? (
              <div style={{
                background: 'white', borderRadius: 12,
                border: '1px solid #e2e8f0', padding: 40, textAlign: 'center'
              }}>
                <p style={{ color: '#64748b' }}>No pending payments</p>
                <p style={{ color: '#94a3b8', fontSize: 12, marginTop: 4 }}>All payments confirmed</p>
              </div>
            ) : (
              pending.map(function(p) {
                return (
                  <div key={p.payment_id} style={{
                    background: 'white', borderRadius: 12,
                    border: '1px solid #e2e8f0', padding: 20, marginBottom: 12
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                          <p style={{ fontWeight: 600, fontSize: 15 }}>{p.tenant_name}</p>
                          {!p.amount_matches && (
                            <span style={{
                              background: '#fef3c7', color: '#92400e',
                              padding: '2px 8px', borderRadius: 4, fontSize: 11
                            }}>Amount mismatch</span>
                          )}
                        </div>
                        <p style={{ color: '#64748b', fontSize: 13, marginBottom: 2 }}>
                          {'₹' + (p.amount ? p.amount.toLocaleString() : '0') + ' · ' + p.month}
                        </p>
                        <p style={{ color: '#94a3b8', fontSize: 12, marginBottom: 2 }}>
                          {'UPI: ' + (p.upi_transaction_id || 'Not captured')}
                        </p>
                        <p style={{ color: '#94a3b8', fontSize: 12 }}>
                          {'Auto-approves: ' + new Date(p.approval_deadline).toLocaleString()}
                        </p>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => handleApprove(p.payment_id)}
                          disabled={actionLoading === p.payment_id} style={{
                            background: '#dcfce7', color: '#166534',
                            padding: '8px 18px', borderRadius: 6,
                            fontSize: 13, fontWeight: 500, border: 'none', cursor: 'pointer'
                          }}>
                          {actionLoading === p.payment_id ? '...' : 'Approve'}
                        </button>
                        <button onClick={() => handleReject(p.payment_id)}
                          disabled={actionLoading === p.payment_id} style={{
                            background: '#fee2e2', color: '#991b1b',
                            padding: '8px 18px', borderRadius: 6,
                            fontSize: 13, fontWeight: 500, border: 'none', cursor: 'pointer'
                          }}>Reject</button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* MANAGE TAB */}
        {activeTab === 'manage' && selectedBuilding && (
          <div>
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', marginBottom: 16
            }}>
              <p style={{ fontSize: 16, fontWeight: 600 }}>
                {selectedBuilding.name} — Units and Tenants
              </p>
              <button onClick={() => { resetForms(); setShowAddUnit(true); }} style={{
                background: '#1a56db', color: 'white',
                padding: '8px 16px', borderRadius: 8,
                fontSize: 13, fontWeight: 500, border: 'none', cursor: 'pointer'
              }}>+ Add Unit</button>
            </div>

            {units.length === 0 ? (
              <div style={{
                background: 'white', borderRadius: 12,
                border: '1px solid #e2e8f0', padding: 40, textAlign: 'center'
              }}>
                <p style={{ color: '#64748b' }}>No units yet</p>
                <button onClick={() => { resetForms(); setShowAddUnit(true); }} style={{
                  marginTop: 12, background: '#1a56db', color: 'white',
                  padding: '8px 20px', borderRadius: 8, border: 'none',
                  fontSize: 14, cursor: 'pointer'
                }}>Add First Unit</button>
              </div>
            ) : (
              units.map(function(unit) {
                const activeTenantsOnly = (unit.tenants || []).filter(t => t.is_active !== false);
                return (
                  <div key={unit.id} style={{
                    background: 'white', borderRadius: 12,
                    border: '1px solid #e2e8f0', padding: 20, marginBottom: 12
                  }}>
                    <div style={{
                      display: 'flex', justifyContent: 'space-between',
                      alignItems: 'center', marginBottom: 12
                    }}>
                      <div>
                        <p style={{ fontWeight: 600, fontSize: 15 }}>
                          {'Unit ' + unit.unit_number}
                          {unit.floor && (
                            <span style={{ color: '#94a3b8', fontWeight: 400, fontSize: 13, marginLeft: 8 }}>
                              {'Floor ' + unit.floor}
                            </span>
                          )}
                          {unit.unit_type === 'lease' && (
                            <span style={{
                              marginLeft: 8, background: '#ede9fe',
                              color: '#6d28d9', fontSize: 11,
                              fontWeight: 600, padding: '2px 8px',
                              borderRadius: 20
                            }}>LEASE</span>
                          )}
                        </p>
                        <p style={{ color: '#64748b', fontSize: 13, marginTop: 2 }}>
                          {(unit.unit_type === 'lease' ? 'Maintenance: ₹' : 'Rent: ₹') + unit.rent_amount.toLocaleString() + '/month'}
                          {unit.unit_type === 'lease' && unit.lease_amount && (
                            <span style={{ marginLeft: 8, color: '#94a3b8' }}>
                              · Lease: ₹{unit.lease_amount.toLocaleString()}
                            </span>
                          )}
                        </p>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => {
                          setSelectedUnitForEdit(unit);
                          setUnitNumber(unit.unit_number);
                          setUnitFloor(unit.floor ? String(unit.floor) : '');
                          setUnitRent(String(unit.rent_amount));
                          setUnitType(unit.unit_type || 'rental');
                          setLeaseAmount(unit.lease_amount ? String(unit.lease_amount) : '');
                          setFormError('');
                          setShowEditUnit(true);
                        }} style={{
                          background: '#f1f5f9', color: '#374151',
                          padding: '7px 14px', borderRadius: 7,
                          fontSize: 13, border: '1px solid #e2e8f0', cursor: 'pointer'
                        }}>Edit</button>
                        <button onClick={() => {
                          resetForms();
                          setSelectedUnitForTenant(unit);
                          setShowAddTenant(true);
                        }} style={{
                          background: '#f0fdf4', color: '#166534',
                          padding: '7px 14px', borderRadius: 7,
                          fontSize: 13, fontWeight: 500,
                          border: '1px solid #bbf7d0', cursor: 'pointer'
                        }}>+ Add Tenant</button>
                        <button onClick={() => {
                          setSelectedUnitForDelete(unit);
                          setFormError('');
                          setShowDeleteUnit(true);
                        }} style={{
                          background: '#fee2e2', color: '#991b1b',
                          padding: '7px 14px', borderRadius: 7,
                          fontSize: 13, border: '1px solid #fca5a5', cursor: 'pointer'
                        }}>Delete</button>
                      </div>
                    </div>

                    {activeTenantsOnly.length > 0 ? (
                      activeTenantsOnly.map(function(t) {
                        return (
                          <div key={t.id} style={{
                            background: '#f8fafc', borderRadius: 8,
                            padding: '12px 14px', marginTop: 8,
                            border: '1px solid #f1f5f9'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                              <div>
                                <p style={{ fontWeight: 500, fontSize: 14 }}>{t.name}</p>
                                <p style={{ color: '#94a3b8', fontSize: 12, marginTop: 2 }}>
                                  {t.phone}
                                  {t.email ? ' · ' + t.email : ''}
                                  {t.aadhaar_last4 ? ' · Aadhaar ****' + t.aadhaar_last4 : ''}
                                  {t.deposit_amount ? ' · Deposit ₹' + t.deposit_amount.toLocaleString() : ''}
                                  {t.move_in_date ? ' · Since ' + t.move_in_date : ''}
                                </p>
                              </div>
                              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                <button
                                  onClick={() => handleViewHistory(t)}
                                  style={{
                                    background: '#eff6ff', color: '#1e40af',
                                    padding: '5px 10px', borderRadius: 6,
                                    fontSize: 12, border: '1px solid #bfdbfe', cursor: 'pointer'
                                  }}>History</button>
                                {t.deposit_amount > 0 && (
                                  <button
                                    onClick={() => handleOpenDepositModal(t)}
                                    style={{
                                      background: '#fdf4ff', color: '#7e22ce',
                                      padding: '5px 10px', borderRadius: 6,
                                      fontSize: 12, border: '1px solid #e9d5ff', cursor: 'pointer'
                                    }}>Deposit</button>
                                )}
                                <button
                                  onClick={() => {
                                    setSelectedTenantForEdit({ ...t, unit_id: unit.id });
                                    setTenantName(t.name);
                                    setTenantPhone(t.phone);
                                    setTenantEmail(t.email || '');
                                    setTenantAadhaar(t.aadhaar_last4 || '');
                                    setTenantDeposit(t.deposit_amount ? String(t.deposit_amount) : '');
                                    setTenantMoveIn(t.move_in_date || '');
                                    setFormError('');
                                    setShowEditTenant(true);
                                  }}
                                  style={{
                                    background: '#f0fdf4', color: '#166534',
                                    padding: '5px 10px', borderRadius: 6,
                                    fontSize: 12, border: '1px solid #bbf7d0', cursor: 'pointer'
                                  }}>Edit</button>
                                <button
                                  onClick={() => {
                                    setSelectedTenantForMoveOut(t);
                                    setMoveOutRefundAmount(t.deposit_amount ? String(t.deposit_amount) : '');
                                    setMoveOutDepositRefunded(false);
                                    setFormError('');
                                    setShowMoveOut(true);
                                  }}
                                  style={{
                                    background: '#fff7ed', color: '#c2410c',
                                    padding: '5px 10px', borderRadius: 6,
                                    fontSize: 12, border: '1px solid #fed7aa', cursor: 'pointer'
                                  }}>Move Out</button>
                                <button
                                  onClick={() => {
                                    setSelectedTenantForDelete(t);
                                    setFormError('');
                                    setShowDeleteTenant(true);
                                  }}
                                  style={{
                                    background: '#fee2e2', color: '#991b1b',
                                    padding: '5px 10px', borderRadius: 6,
                                    fontSize: 12, border: '1px solid #fca5a5', cursor: 'pointer'
                                  }}>Delete</button>
                                <span style={{
                                  background: '#dbeafe', color: '#1e40af',
                                  padding: '3px 10px', borderRadius: 20,
                                  fontSize: 12, fontWeight: 500
                                }}>Active</span>
                              </div>
                            </div>

                            {/* Pro-rata nudge banner */}
                            {isMidMonthMoveIn(t.move_in_date) && !prorataDismissed[t.id] && (() => {
                              const pr = calcProrata(t.move_in_date, unit.rent_amount);
                              // Only show if move-in is current month and no payment recorded yet
                              const now = new Date();
                              const moveIn = new Date(t.move_in_date);
                              const isSameMonth = moveIn.getMonth() === now.getMonth() && moveIn.getFullYear() === now.getFullYear();
                              if (!isSameMonth) return null;
                              return (
                                <div style={{
                                  marginTop: 10,
                                  background: '#fffbeb', borderRadius: 8,
                                  border: '1px solid #fde68a', padding: '12px 14px'
                                }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
                                    <div>
                                      <p style={{ fontSize: 13, fontWeight: 600, color: '#92400e', margin: '0 0 3px' }}>
                                        Mid-month move-in · {t.move_in_date}
                                      </p>
                                      <p style={{ fontSize: 12, color: '#92400e', margin: 0, opacity: 0.85 }}>
                                        {pr.days} days remaining in month · Pro-rata = <strong>₹{pr.amount.toLocaleString()}</strong>
                                        &nbsp;<span style={{ opacity: 0.65 }}>(₹{unit.rent_amount.toLocaleString()} ÷ {pr.daysInMonth} × {pr.days})</span>
                                      </p>
                                    </div>
                                    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                                      <button
                                        onClick={() => setProrataDismissed(prev => ({ ...prev, [t.id]: true }))}
                                        style={{
                                          background: 'white', color: '#92400e',
                                          border: '1px solid #fde68a', borderRadius: 6,
                                          padding: '5px 10px', fontSize: 12, cursor: 'pointer'
                                        }}>
                                        Skip → start {(() => { const d = new Date(t.move_in_date); return new Date(d.getFullYear(), d.getMonth()+1, 1).toLocaleString('default', { month: 'short' }); })()}
                                      </button>
                                      <button
                                        onClick={() => handleOpenProrataModal(t, unit)}
                                        style={{
                                          background: '#f59e0b', color: 'white',
                                          border: 'none', borderRadius: 6,
                                          padding: '5px 12px', fontSize: 12,
                                          fontWeight: 600, cursor: 'pointer'
                                        }}>
                                        Record ₹{pr.amount.toLocaleString()}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        );
                      })
                    ) : (
                      <p style={{ color: '#94a3b8', fontSize: 13, fontStyle: 'italic', marginTop: 4 }}>
                        No tenant — unit is vacant
                      </p>
                    )}
                  </div>
                );
              })
            )}

            {/* Recently moved out tenants — shown for 90 days */}
            {recentMoveouts.length > 0 && (
              <div style={{ marginTop: 24 }}>
                <p style={{
                  fontSize: 13, fontWeight: 600, color: '#92400e',
                  marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8
                }}>
                  Recently Moved Out
                  <span style={{
                    background: '#fef3c7', color: '#92400e',
                    padding: '2px 8px', borderRadius: 20,
                    fontSize: 11, fontWeight: 500
                  }}>Retained for 90 days</span>
                </p>
                {recentMoveouts.map(function(t) {
                  return (
                    <div key={t.id} style={{
                      background: 'white', borderRadius: 10,
                      border: '1px solid #fde68a',
                      borderLeft: '3px solid #f59e0b',
                      padding: '12px 16px', marginBottom: 8,
                      opacity: 0.85
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                        <div>
                          <p style={{ fontWeight: 500, fontSize: 14 }}>{t.name}</p>
                          <p style={{ color: '#94a3b8', fontSize: 12, marginTop: 2 }}>
                            {t.phone}
                            {t.move_out_date ? ' · Moved out ' + t.move_out_date : ''}
                            {t.deposit_amount ? ' · Deposit ₹' + t.deposit_amount.toLocaleString() : ''}
                          </p>
                          <p style={{ color: '#d97706', fontSize: 11, marginTop: 3 }}>
                            {'Auto-removes in ' + t.days_until_archived + ' day(s)'}
                          </p>
                        </div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <button
                            onClick={() => handleViewHistory(t)}
                            style={{
                              background: '#eff6ff', color: '#1e40af',
                              padding: '5px 10px', borderRadius: 6,
                              fontSize: 12, border: '1px solid #bfdbfe', cursor: 'pointer'
                            }}>History</button>
                          <span style={{
                            background: '#fef3c7', color: '#92400e',
                            padding: '3px 10px', borderRadius: 20,
                            fontSize: 11, fontWeight: 500
                          }}>Moved Out</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ARREARS TAB */}
        {activeTab === 'arrears' && (
          <div>
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', marginBottom: 16
            }}>
              <p style={{ fontSize: 16, fontWeight: 600 }}>
                Outstanding Dues — {selectedBuilding?.name}
              </p>
              {arrearsData && arrearsData.tenants_with_arrears > 0 && (
                <button
                  onClick={handleRemindAllArrears}
                  disabled={arrearsLoading}
                  style={{
                    background: '#f59e0b', color: 'white',
                    padding: '8px 16px', borderRadius: 8,
                    fontSize: 13, fontWeight: 500,
                    border: 'none', cursor: 'pointer'
                  }}>
                  {arrearsLoading ? 'Sending...' : 'Send All Reminders'}
                </button>
              )}
            </div>

            {arrearsData && arrearsData.total_outstanding > 0 && (
              <div style={{
                background: '#fef3c7', borderRadius: 12,
                border: '1px solid #fde68a',
                padding: '16px 20px', marginBottom: 20,
                display: 'flex', justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <p style={{ fontSize: 13, color: '#92400e', marginBottom: 4 }}>
                    Total Outstanding
                  </p>
                  <p style={{ fontSize: 24, fontWeight: 700, color: '#92400e' }}>
                    {'₹' + arrearsData.total_outstanding.toLocaleString()}
                  </p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <p style={{ fontSize: 13, color: '#92400e', marginBottom: 4 }}>
                    Tenants with dues
                  </p>
                  <p style={{ fontSize: 24, fontWeight: 700, color: '#92400e' }}>
                    {arrearsData.tenants_with_arrears}
                  </p>
                </div>
              </div>
            )}

            {!arrearsData || arrearsData.tenants_with_arrears === 0 ? (
              <div style={{
                background: 'white', borderRadius: 12,
                border: '1px solid #e2e8f0',
                padding: 40, textAlign: 'center'
              }}>
                <p style={{ fontSize: 20 }}>✓</p>
                <p style={{ color: '#16a34a', fontWeight: 600, marginTop: 8 }}>
                  All tenants are up to date
                </p>
                <p style={{ color: '#94a3b8', fontSize: 12, marginTop: 4 }}>
                  No outstanding dues in the last 6 months
                </p>
              </div>
            ) : (
              arrearsData.arrears.map(function(t) {
                return (
                  <div key={t.tenant_id} style={{
                    background: 'white', borderRadius: 12,
                    border: '1px solid #e2e8f0',
                    padding: 20, marginBottom: 12
                  }}>
                    <div style={{
                      display: 'flex', justifyContent: 'space-between',
                      alignItems: 'flex-start'
                    }}>
                      <div>
                        <p style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>
                          {t.tenant_name}
                        </p>
                        <p style={{ color: '#64748b', fontSize: 13, marginBottom: 8 }}>
                          {'Unit ' + t.unit_number + ' · ₹' + t.rent_amount.toLocaleString() + (t.unit_type === 'lease' ? '/month maintenance' : '/month')}
                        </p>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {t.arrear_months.map(function(m) {
                            const isUpcoming = m.status === 'upcoming';
                            const isOverdue = !isUpcoming && m.days_overdue > 30;
                            return (
                              <span key={m.month} style={{
                                background: isUpcoming ? '#dbeafe' : isOverdue ? '#fee2e2' : '#fef9c3',
                                color: isUpcoming ? '#1e40af' : isOverdue ? '#991b1b' : '#854d0e',
                                padding: '3px 10px', borderRadius: 20,
                                fontSize: 11, fontWeight: 500
                              }}>
                                {m.month}
                                {isUpcoming ? ' — upcoming' : isOverdue ? ' — ' + m.days_overdue + 'd overdue' : ' — due'}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <p style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>
                          Total outstanding
                        </p>
                        <p style={{
                          fontSize: 20, fontWeight: 700, color: '#dc2626',
                          marginBottom: 12
                        }}>
                          {'₹' + t.total_outstanding.toLocaleString()}
                        </p>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                          <button
                            onClick={() => handleSendReminder({
                              id: t.tenant_id, name: t.tenant_name
                            })}
                            disabled={actionLoading === 'remind-' + t.tenant_id}
                            style={{
                              background: '#fef9c3', color: '#854d0e',
                              padding: '6px 12px', borderRadius: 6,
                              fontSize: 12, border: '1px solid #fde68a',
                              cursor: 'pointer'
                            }}>
                            Remind
                          </button>
                          <button
                            onClick={() => handleViewTenantArrears(t)}
                            style={{
                              background: '#eff6ff', color: '#1e40af',
                              padding: '6px 12px', borderRadius: 6,
                              fontSize: 12, border: '1px solid #bfdbfe',
                              cursor: 'pointer'
                            }}>
                            Details
                          </button>
                          <button
                            onClick={() => {
                              setSelectedTenantForPayment({
                                id: t.tenant_id, name: t.tenant_name
                              });
                              setManualMonth(t.arrear_months[0]?.month || currentMonth);
                              setManualAmount(String(t.rent_amount));
                              setManualDate(new Date().toISOString().split('T')[0]);
                              setFormError('');
                              setShowManualPayment(true);
                            }}
                            style={{
                              background: '#dbeafe', color: '#1e40af',
                              padding: '6px 12px', borderRadius: 6,
                              fontSize: 12, border: '1px solid #bfdbfe',
                              cursor: 'pointer'
                            }}>
                            Record
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* ADD BUILDING MODAL */}
      {showAddBuilding && (
        <Modal title="Add Building"
          onClose={() => { setShowAddBuilding(false); resetForms(); }}
          onSave={handleAddBuilding} saveLabel="Save Building"
          formError={formError} formLoading={formLoading}>
          <Field label="Building Name">
            <input type="text" placeholder="e.g. Hashvi Apartments"
              value={buildingName} onChange={e => setBuildingName(e.target.value)} style={inputStyle} />
          </Field>
          <Field label="Full Address">
            <textarea placeholder="Street, Area, City, Pincode"
              value={buildingAddress} onChange={e => setBuildingAddress(e.target.value)}
              rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
          </Field>
        </Modal>
      )}

      {/* ADD UNIT MODAL */}
      {showAddUnit && (
        <Modal title={'Add Unit — ' + selectedBuilding?.name}
          onClose={() => { setShowAddUnit(false); resetForms(); }}
          onSave={handleAddUnit} saveLabel="Save Unit"
          formError={formError} formLoading={formLoading}>

          {/* Unit Type Toggle */}
          <Field label="Unit Type">
            <div style={{ display: 'flex', gap: 10 }}>
              {['rental', 'lease'].map(type => (
                <button key={type} onClick={() => setUnitType(type)} style={{
                  flex: 1, padding: '10px',
                  background: unitType === type ? '#1a56db' : '#f1f5f9',
                  color: unitType === type ? 'white' : '#64748b',
                  border: unitType === type ? 'none' : '1px solid #e2e8f0',
                  borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer'
                }}>
                  {type === 'rental' ? '🏠 Rental' : '📋 Lease'}
                </button>
              ))}
            </div>
            <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
              {unitType === 'rental'
                ? 'Monthly rent collected from tenant'
                : 'One-time lease amount + monthly maintenance collected'}
            </p>
          </Field>

          <Field label="Unit Number">
            <input type="text" placeholder="e.g. 101, Room 1"
              value={unitNumber} onChange={e => setUnitNumber(e.target.value)} style={inputStyle} />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Floor (optional)">
              <input type="number" placeholder="1"
                value={unitFloor} onChange={e => setUnitFloor(e.target.value)} style={inputStyle} />
            </Field>
            <Field label={unitType === 'lease' ? 'Monthly Maintenance (₹)' : 'Monthly Rent (₹)'}>
              <input type="number" placeholder={unitType === 'lease' ? '1500' : '7000'}
                value={unitRent} onChange={e => setUnitRent(e.target.value)} style={inputStyle} />
            </Field>
          </div>
          {unitType === 'lease' && (
            <Field label="Lease Amount (₹) — one-time">
              <input type="number" placeholder="500000"
                value={leaseAmount} onChange={e => setLeaseAmount(e.target.value)} style={inputStyle} />
            </Field>
          )}
        </Modal>
      )}

      {/* EDIT UNIT MODAL */}
      {showEditUnit && selectedUnitForEdit && (
        <Modal title={'Edit Unit ' + selectedUnitForEdit.unit_number}
          onClose={() => { setShowEditUnit(false); resetForms(); }}
          onSave={handleEditUnit} saveLabel="Update Unit"
          formError={formError} formLoading={formLoading}>
          <div style={{
            background: '#fef9c3', borderRadius: 8,
            padding: '10px 14px', marginBottom: 14,
            fontSize: 12, color: '#854d0e',
            border: '1px solid #fde68a'
          }}>
            If monthly amount changes, tenant will be notified on WhatsApp automatically.
          </div>

          {/* Unit Type Toggle */}
          <Field label="Unit Type">
            <div style={{ display: 'flex', gap: 10 }}>
              {['rental', 'lease'].map(type => (
                <button key={type} onClick={() => setUnitType(type)} style={{
                  flex: 1, padding: '10px',
                  background: unitType === type ? '#1a56db' : '#f1f5f9',
                  color: unitType === type ? 'white' : '#64748b',
                  border: unitType === type ? 'none' : '1px solid #e2e8f0',
                  borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer'
                }}>
                  {type === 'rental' ? '🏠 Rental' : '📋 Lease'}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Unit Number">
            <input type="text" value={unitNumber}
              onChange={e => setUnitNumber(e.target.value)} style={inputStyle} />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Floor (optional)">
              <input type="number" value={unitFloor}
                onChange={e => setUnitFloor(e.target.value)} style={inputStyle} />
            </Field>
            <Field label={unitType === 'lease' ? 'Monthly Maintenance (₹)' : 'Monthly Rent (₹)'}>
              <input type="number" value={unitRent}
                onChange={e => setUnitRent(e.target.value)} style={inputStyle} />
            </Field>
          </div>
          {unitType === 'lease' && (
            <Field label="Lease Amount (₹) — one-time">
              <input type="number" placeholder="500000"
                value={leaseAmount} onChange={e => setLeaseAmount(e.target.value)} style={inputStyle} />
            </Field>
          )}
        </Modal>
      )}

      {/* ADD TENANT MODAL */}
      {showAddTenant && selectedUnitForTenant && (
        <Modal title={'Add Tenant — Unit ' + selectedUnitForTenant.unit_number}
          onClose={() => { setShowAddTenant(false); resetForms(); }}
          onSave={handleAddTenant} saveLabel="Save Tenant"
          formError={formError} formLoading={formLoading}>
          <Field label="Full Name">
            <input type="text" placeholder="As it should appear on receipts"
              value={tenantName} onChange={e => setTenantName(e.target.value)} style={inputStyle} />
          </Field>
          <Field label="WhatsApp Number">
            <input type="tel" placeholder="10-digit number"
              value={tenantPhone}
              onChange={e => setTenantPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
              style={inputStyle} />
          </Field>
          {/* NEW: Email field */}
          <Field label="Email (optional)">
            <input type="email" placeholder="tenant@email.com"
              value={tenantEmail}
              onChange={e => setTenantEmail(e.target.value)}
              style={inputStyle} />
          </Field>
          {/* NEW: Aadhaar last 4 field */}
          <Field label="Aadhaar Last 4 Digits (optional)">
            <input type="text" placeholder="e.g. 4521" maxLength={4}
              value={tenantAadhaar}
              onChange={e => setTenantAadhaar(e.target.value.replace(/\D/g, '').slice(0, 4))}
              style={inputStyle} />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Security Deposit (₹)">
              <input type="number" placeholder="21000"
                value={tenantDeposit} onChange={e => setTenantDeposit(e.target.value)} style={inputStyle} />
            </Field>
            <Field label="Move-in Date">
              <input type="date" value={tenantMoveIn}
                onChange={e => setTenantMoveIn(e.target.value)} style={inputStyle} />
            </Field>
          </div>
        </Modal>
      )}

      {/* MANUAL PAYMENT MODAL */}
      {showManualPayment && selectedTenantForPayment && (
        <Modal title={'Record Payment — ' + selectedTenantForPayment.name}
          onClose={() => { setShowManualPayment(false); resetForms(); }}
          onSave={handleManualPayment} saveLabel="Record Payment"
          formError={formError} formLoading={formLoading}>
          <div style={{
            background: '#f0fdf4', borderRadius: 8,
            padding: '10px 14px', marginBottom: 16,
            fontSize: 13, color: '#166534',
            border: '1px solid #bbf7d0'
          }}>
            Tenant will receive receipt on WhatsApp after recording.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Amount (₹)">
              <input type="number" placeholder="7000"
                value={manualAmount} onChange={e => setManualAmount(e.target.value)} style={inputStyle} />
            </Field>
            <Field label="Payment Date">
              <input type="date" value={manualDate}
                onChange={e => setManualDate(e.target.value)} style={inputStyle} />
            </Field>
          </div>
          <Field label="Month (e.g. March-2026)">
            <input type="text" placeholder="March-2026"
              value={manualMonth} onChange={e => setManualMonth(e.target.value)} style={inputStyle} />
          </Field>
          <Field label="Payment Mode">
            <select value={manualMode} onChange={e => { setManualMode(e.target.value); setManualRef(''); }}
              style={{ ...inputStyle, background: 'white' }}>
              <option>Cash</option>
              <option>UPI</option>
              <option>Bank Transfer</option>
              <option>Cheque</option>
            </select>
          </Field>
          {manualMode !== 'Cash' && (
            <Field label={refLabel()}>
              <input type="text" placeholder={refPlaceholder()}
                value={manualRef} onChange={e => setManualRef(e.target.value)} style={inputStyle} />
            </Field>
          )}
        </Modal>
      )}

      {/* MOVE OUT MODAL */}
      {showMoveOut && selectedTenantForMoveOut && (
        <Modal
          title={'Move Out — ' + selectedTenantForMoveOut.name}
          onClose={() => { setShowMoveOut(false); resetForms(); }}
          onSave={handleMoveOut}
          saveLabel="Confirm Move Out"
          formError={formError}
          formLoading={formLoading}
        >
          <div style={{
            background: '#fff7ed', borderRadius: 8,
            padding: '12px 14px', marginBottom: 16,
            border: '1px solid #fed7aa'
          }}>
            <p style={{ color: '#c2410c', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
              This will mark {selectedTenantForMoveOut.name} as moved out
            </p>
            <p style={{ color: '#92400e', fontSize: 12 }}>
              Their payment history and receipts remain accessible. Unit will show as vacant.
            </p>
          </div>

          {selectedTenantForMoveOut.deposit_amount > 0 ? (
            <>
              <div style={{
                background: '#f8fafc', borderRadius: 8,
                padding: '12px 14px', marginBottom: 16,
                border: '1px solid #e2e8f0'
              }}>
                <p style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>
                  Security Deposit on Record
                </p>
                <p style={{ fontSize: 20, fontWeight: 700, color: '#1e293b' }}>
                  {'₹' + selectedTenantForMoveOut.deposit_amount.toLocaleString()}
                </p>
              </div>

              <Field label="Deposit Status">
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    onClick={() => setMoveOutDepositRefunded(false)}
                    style={{
                      flex: 1, padding: '10px',
                      background: !moveOutDepositRefunded ? '#fee2e2' : '#f1f5f9',
                      color: !moveOutDepositRefunded ? '#991b1b' : '#64748b',
                      border: !moveOutDepositRefunded ? '1.5px solid #fca5a5' : '1px solid #e2e8f0',
                      borderRadius: 8, fontSize: 13,
                      fontWeight: !moveOutDepositRefunded ? 600 : 400,
                      cursor: 'pointer'
                    }}>
                    Pending Refund
                  </button>
                  <button
                    onClick={() => setMoveOutDepositRefunded(true)}
                    style={{
                      flex: 1, padding: '10px',
                      background: moveOutDepositRefunded ? '#dcfce7' : '#f1f5f9',
                      color: moveOutDepositRefunded ? '#166534' : '#64748b',
                      border: moveOutDepositRefunded ? '1.5px solid #86efac' : '1px solid #e2e8f0',
                      borderRadius: 8, fontSize: 13,
                      fontWeight: moveOutDepositRefunded ? 600 : 400,
                      cursor: 'pointer'
                    }}>
                    Already Refunded
                  </button>
                </div>
              </Field>

              <Field label="Refund Amount (₹)">
                <input
                  type="number"
                  value={moveOutRefundAmount}
                  onChange={e => setMoveOutRefundAmount(e.target.value)}
                  style={inputStyle}
                />
              </Field>
            </>
          ) : (
            <p style={{ color: '#64748b', fontSize: 13, marginBottom: 8 }}>
              No security deposit on record for this tenant.
            </p>
          )}

          <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 8 }}>
            A move-out confirmation will be sent to the tenant on WhatsApp.
          </p>
        </Modal>
      )}

      {/* EDIT TENANT MODAL */}
      {showEditTenant && selectedTenantForEdit && (
        <Modal title={'Edit Tenant — ' + selectedTenantForEdit.name}
          onClose={() => { setShowEditTenant(false); resetForms(); }}
          onSave={handleEditTenant} saveLabel="Update Tenant"
          formError={formError} formLoading={formLoading}>
          <Field label="Full Name">
            <input type="text" placeholder="As it should appear on receipts"
              value={tenantName} onChange={e => setTenantName(e.target.value)} style={inputStyle} />
          </Field>
          <Field label="WhatsApp Number">
            <input type="tel" placeholder="10-digit number"
              value={tenantPhone}
              onChange={e => setTenantPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
              style={inputStyle} />
          </Field>
          <Field label="Email (optional)">
            <input type="email" placeholder="tenant@email.com"
              value={tenantEmail} onChange={e => setTenantEmail(e.target.value)} style={inputStyle} />
          </Field>
          <Field label="Aadhaar Last 4 Digits (optional)">
            <input type="text" placeholder="e.g. 7890" maxLength={4}
              value={tenantAadhaar}
              onChange={e => setTenantAadhaar(e.target.value.replace(/\D/g, '').slice(0, 4))}
              style={inputStyle} />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Security Deposit (₹)">
              <input type="number" placeholder="0"
                value={tenantDeposit} onChange={e => setTenantDeposit(e.target.value)} style={inputStyle} />
            </Field>
            <Field label="Move-in Date">
              <input type="date"
                value={tenantMoveIn} onChange={e => setTenantMoveIn(e.target.value)} style={inputStyle} />
            </Field>
          </div>
        </Modal>
      )}

      {/* DELETE TENANT CONFIRM MODAL */}
      {showDeleteTenant && selectedTenantForDelete && (
        <Modal title="Delete Tenant"
          onClose={() => { setShowDeleteTenant(false); resetForms(); }}
          onSave={handleDeleteTenant} saveLabel="Yes, Delete"
          formError={formError} formLoading={formLoading}>
          <div style={{
            background: '#fee2e2', borderRadius: 8,
            padding: '14px 16px', marginBottom: 8,
            fontSize: 14, color: '#991b1b',
            border: '1px solid #fca5a5'
          }}>
            <strong>Are you sure you want to delete {selectedTenantForDelete.name}?</strong>
            <p style={{ marginTop: 8, fontSize: 13 }}>
              This cannot be undone. Tenants with payment history cannot be deleted — use Move Out instead.
            </p>
          </div>
        </Modal>
      )}

      {/* DELETE UNIT CONFIRM MODAL */}
      {showDeleteUnit && selectedUnitForDelete && (
        <Modal title="Delete Unit"
          onClose={() => { setShowDeleteUnit(false); resetForms(); }}
          onSave={handleDeleteUnit} saveLabel="Yes, Delete"
          formError={formError} formLoading={formLoading}>
          <div style={{
            background: '#fee2e2', borderRadius: 8,
            padding: '14px 16px', marginBottom: 8,
            fontSize: 14, color: '#991b1b',
            border: '1px solid #fca5a5'
          }}>
            <strong>Are you sure you want to delete Unit {selectedUnitForDelete.unit_number}?</strong>
            <p style={{ marginTop: 8, fontSize: 13 }}>
              This cannot be undone. Units with tenant records cannot be deleted — remove all tenants first.
            </p>
          </div>
        </Modal>
      )}

      {/* PAYMENT HISTORY MODAL */}
      {showHistory && selectedTenantForHistory && (
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 1000, padding: 20
        }}>
          <div style={{
            background: 'white', borderRadius: 12,
            padding: 28, width: '100%', maxWidth: 520,
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            maxHeight: '80vh', overflowY: 'auto'
          }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', marginBottom: 20
            }}>
              <h3 style={{ fontSize: 17, fontWeight: 600 }}>
                {selectedTenantForHistory.name} — Payment History
              </h3>
              <button onClick={() => setShowHistory(false)} style={{
                background: 'none', border: 'none',
                fontSize: 20, cursor: 'pointer', color: '#94a3b8'
              }}>✕</button>
            </div>

            {paymentHistory.length === 0 ? (
              <p style={{ color: '#64748b', textAlign: 'center', padding: 20 }}>
                No payments recorded yet
              </p>
            ) : (
              paymentHistory.map(function(p) {
                const sc = {
                  paid: { bg: '#dcfce7', color: '#166534' },
                  auto_approved: { bg: '#d1fae5', color: '#065f46' },
                  pending: { bg: '#fef9c3', color: '#854d0e' },
                  rejected: { bg: '#fee2e2', color: '#991b1b' }
                }[p.status] || { bg: '#f1f5f9', color: '#64748b' };

                return (
                  <div key={p.id} style={{
                    padding: '14px 0', borderBottom: '1px solid #f1f5f9'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <p style={{ fontWeight: 600, fontSize: 14 }}>{p.month}</p>
                        <p style={{ color: '#64748b', fontSize: 13, marginTop: 2 }}>
                          {'₹' + p.amount.toLocaleString() + ' · ' + (p.payment_mode || 'UPI')}
                        </p>
                        {p.upi_transaction_id && (
                          <p style={{ color: '#94a3b8', fontSize: 12, marginTop: 2 }}>
                            {'Ref: ' + p.upi_transaction_id}
                          </p>
                        )}
                        {p.receipt_number && (
                          <p style={{ color: '#94a3b8', fontSize: 12, marginTop: 2 }}>
                            {p.receipt_number}
                          </p>
                        )}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                        <span style={{
                          background: sc.bg, color: sc.color,
                          padding: '3px 10px', borderRadius: 20,
                          fontSize: 11, fontWeight: 600,
                          textTransform: 'uppercase'
                        }}>{p.status.replace('_', ' ')}</span>
                        {p.receipt_number && (
                          <a href={process.env.REACT_APP_API_URL + '/receipt/' + p.id}
                            target="_blank" rel="noreferrer"
                            style={{ color: '#1a56db', fontSize: 12 }}>
                            Download
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* PRO-RATA MODAL */}
      {showProrataModal && prorataData && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, padding: 20
        }}>
          <div style={{
            background: 'white', borderRadius: 12, padding: 28,
            width: '100%', maxWidth: 440,
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ fontSize: 17, fontWeight: 600 }}>Pro-rata Rent · {prorataData.tenant.name}</h3>
              <button onClick={() => { setShowProrataModal(false); resetForms(); }}
                style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#94a3b8' }}>✕</button>
            </div>

            <div style={{
              background: '#fffbeb', border: '1px solid #fde68a',
              borderRadius: 8, padding: '12px 14px', marginBottom: 16,
              fontSize: 13, color: '#92400e'
            }}>
              {prorataData.month} pro-rata: {prorataData.days} days
              ({prorataData.tenant.move_in_date} – end of month) at
              ₹{prorataData.unit.rent_amount.toLocaleString()}/month = <strong>₹{prorataData.amount.toLocaleString()}</strong>
            </div>

            <Field label="Amount (₹) — editable">
              <input type="number" value={manualAmount}
                onChange={e => setManualAmount(e.target.value)} style={inputStyle} />
              <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                Adjust if your agreed amount differs from the calculated amount.
              </p>
            </Field>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Payment Date">
                <input type="date" value={manualDate}
                  onChange={e => setManualDate(e.target.value)} style={inputStyle} />
              </Field>
              <Field label="Month">
                <input type="text" value={manualMonth}
                  onChange={e => setManualMonth(e.target.value)} style={inputStyle} />
              </Field>
            </div>

            <Field label="Payment Mode">
              <select value={manualMode}
                onChange={e => { setManualMode(e.target.value); setManualRef(''); }}
                style={{ ...inputStyle, background: 'white' }}>
                <option>Cash</option>
                <option>UPI</option>
                <option>Bank Transfer</option>
                <option>Cheque</option>
              </select>
            </Field>

            {manualMode !== 'Cash' && (
              <Field label={refLabel()}>
                <input type="text" placeholder={refPlaceholder()}
                  value={manualRef} onChange={e => setManualRef(e.target.value)} style={inputStyle} />
              </Field>
            )}

            {formError && (
              <p style={{ color: '#dc2626', fontSize: 13, marginTop: 8 }}>{formError}</p>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button onClick={() => { setShowProrataModal(false); resetForms(); }} style={{
                flex: 1, padding: 11, background: '#f1f5f9',
                border: 'none', borderRadius: 8, fontSize: 14, cursor: 'pointer', color: '#64748b'
              }}>Cancel</button>
              <button onClick={handleSaveProrata} disabled={formLoading} style={{
                flex: 2, padding: 11,
                background: formLoading ? '#93c5fd' : '#1a56db',
                color: 'white', border: 'none', borderRadius: 8,
                fontSize: 14, fontWeight: 600, cursor: 'pointer'
              }}>
                {formLoading ? 'Saving...' : 'Save & send WhatsApp receipt'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DEPOSIT PAYMENT MODAL */}
      {showDepositModal && selectedTenantForDeposit && (
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 1000, padding: 20
        }}>
          <div style={{
            background: 'white', borderRadius: 12,
            padding: 28, width: '100%', maxWidth: 500,
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            maxHeight: '90vh', overflowY: 'auto'
          }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ fontSize: 17, fontWeight: 600 }}>
                {selectedTenantForDeposit.name} — Deposit
              </h3>
              <button onClick={() => { setShowDepositModal(false); resetForms(); }} style={{
                background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#94a3b8'
              }}>✕</button>
            </div>

            {/* Summary bar */}
            {depositHistory && (
              <div style={{
                background: depositHistory.fully_paid ? '#f0fdf4' : '#fdf4ff',
                border: '1px solid ' + (depositHistory.fully_paid ? '#86efac' : '#e9d5ff'),
                borderRadius: 10, padding: '14px 18px', marginBottom: 20,
                display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12
              }}>
                {[
                  { label: 'Total Deposit', value: '₹' + (depositHistory.total_agreed || 0).toLocaleString(), color: '#1e293b' },
                  { label: 'Collected', value: '₹' + (depositHistory.total_collected || 0).toLocaleString(), color: '#16a34a' },
                  { label: 'Balance Due', value: '₹' + (depositHistory.balance_remaining || 0).toLocaleString(), color: depositHistory.fully_paid ? '#16a34a' : '#7e22ce' },
                ].map((s, i) => (
                  <div key={i} style={{ textAlign: 'center' }}>
                    <p style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>{s.label}</p>
                    <p style={{ fontSize: 16, fontWeight: 700, color: s.color }}>{s.value}</p>
                  </div>
                ))}
              </div>
            )}
            {depositHistory && depositHistory.fully_paid && (
              <div style={{
                background: '#f0fdf4', border: '1px solid #86efac',
                borderRadius: 8, padding: '10px 14px', marginBottom: 16,
                color: '#166534', fontSize: 13, fontWeight: 500, textAlign: 'center'
              }}>
                ✓ Deposit fully collected
              </div>
            )}

            {/* Record new instalment */}
            {depositHistory && !depositHistory.fully_paid && (
              <div style={{
                background: '#f8fafc', borderRadius: 8,
                border: '1px solid #e2e8f0', padding: 16, marginBottom: 20
              }}>
                <p style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 12 }}>
                  Record Instalment
                  <span style={{ marginLeft: 8, fontWeight: 400, color: '#94a3b8', fontSize: 12 }}>
                    Balance: ₹{(depositHistory.balance_remaining || 0).toLocaleString()}
                  </span>
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 4 }}>Amount (₹)</label>
                    <input type="number" placeholder="e.g. 5000"
                      value={depositInstalment}
                      onChange={e => setDepositInstalment(e.target.value)}
                      style={{ width: '100%', padding: '9px 11px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 14, boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 4 }}>Date</label>
                    <input type="date"
                      value={depositDate}
                      onChange={e => setDepositDate(e.target.value)}
                      style={{ width: '100%', padding: '9px 11px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 14, boxSizing: 'border-box' }} />
                  </div>
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 12, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 4 }}>Mode</label>
                  <select value={depositMode} onChange={e => setDepositMode(e.target.value)}
                    style={{ width: '100%', padding: '9px 11px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 14, background: 'white', boxSizing: 'border-box' }}>
                    {['Cash', 'UPI', 'Bank Transfer', 'Cheque'].map(m => <option key={m}>{m}</option>)}
                  </select>
                </div>
                {depositMode !== 'Cash' && (
                  <div style={{ marginBottom: 10 }}>
                    <label style={{ fontSize: 12, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 4 }}>
                      {depositMode === 'UPI' ? 'UPI Transaction ID' : depositMode === 'Cheque' ? 'Cheque Number' : 'UTR / Reference'}
                    </label>
                    <input type="text"
                      value={depositRef}
                      onChange={e => setDepositRef(e.target.value)}
                      placeholder={depositMode === 'UPI' ? 'e.g. 606711011862' : ''}
                      style={{ width: '100%', padding: '9px 11px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 14, boxSizing: 'border-box' }} />
                  </div>
                )}
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 12, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 4 }}>Note (optional)</label>
                  <input type="text"
                    value={depositNote}
                    onChange={e => setDepositNote(e.target.value)}
                    placeholder="e.g. First instalment"
                    style={{ width: '100%', padding: '9px 11px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 14, boxSizing: 'border-box' }} />
                </div>
                {depositError && (
                  <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 10 }}>{depositError}</p>
                )}
                <button
                  onClick={handleRecordDeposit}
                  disabled={depositLoading}
                  style={{
                    width: '100%', padding: 11,
                    background: depositLoading ? '#a78bfa' : '#7c3aed',
                    color: 'white', border: 'none', borderRadius: 8,
                    fontSize: 14, fontWeight: 600, cursor: 'pointer'
                  }}>
                  {depositLoading ? 'Saving...' : 'Record Instalment'}
                </button>
              </div>
            )}

            {/* Instalment history */}
            {depositHistory && depositHistory.instalments.length > 0 && (
              <div>
                <p style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 10 }}>
                  Payment History ({depositHistory.instalments.length})
                </p>
                {depositHistory.instalments.map(function(d) {
                  return (
                    <div key={d.id} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                      padding: '10px 0', borderBottom: '1px solid #f1f5f9'
                    }}>
                      <div>
                        <p style={{ fontWeight: 600, fontSize: 14, color: '#16a34a' }}>
                          ₹{d.amount_paid.toLocaleString()}
                        </p>
                        <p style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>
                          {d.payment_date} · {d.payment_mode}
                          {d.reference ? ' · Ref: ' + d.reference : ''}
                        </p>
                        {d.note && (
                          <p style={{ color: '#94a3b8', fontSize: 12, marginTop: 1 }}>{d.note}</p>
                        )}
                      </div>
                      <button
                        onClick={() => handleDeleteDepositPayment(d.id)}
                        style={{
                          background: 'none', border: 'none',
                          color: '#cbd5e1', fontSize: 16, cursor: 'pointer',
                          padding: '2px 6px'
                        }}
                        title="Remove this entry">×</button>
                    </div>
                  );
                })}
              </div>
            )}

            {depositHistory && depositHistory.instalments.length === 0 && (
              <p style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: '12px 0' }}>
                No deposit payments recorded yet.
              </p>
            )}

            <button onClick={() => { setShowDepositModal(false); resetForms(); }} style={{
              width: '100%', padding: 11, background: '#f1f5f9',
              border: 'none', borderRadius: 8, fontSize: 14,
              cursor: 'pointer', color: '#64748b', marginTop: 16
            }}>Close</button>
          </div>
        </div>
      )}

      {/* TENANT ARREARS DETAIL MODAL */}
      {showTenantArrears && tenantArrearsDetail && (
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 1000, padding: 20
        }}>
          <div style={{
            background: 'white', borderRadius: 12,
            padding: 28, width: '100%', maxWidth: 460,
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            maxHeight: '80vh', overflowY: 'auto'
          }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', marginBottom: 20
            }}>
              <h3 style={{ fontSize: 17, fontWeight: 600 }}>
                {tenantArrearsDetail.tenant_name} — Arrears
              </h3>
              <button onClick={() => setShowTenantArrears(false)} style={{
                background: 'none', border: 'none',
                fontSize: 20, cursor: 'pointer', color: '#94a3b8'
              }}>✕</button>
            </div>

            <div style={{
              background: '#fef3c7', borderRadius: 8,
              padding: '12px 16px', marginBottom: 20,
              border: '1px solid #fde68a'
            }}>
              <p style={{ fontSize: 12, color: '#92400e', marginBottom: 2 }}>
                Total Outstanding
              </p>
              <p style={{ fontSize: 22, fontWeight: 700, color: '#92400e' }}>
                {'₹' + tenantArrearsDetail.total_outstanding.toLocaleString()}
              </p>
              <p style={{ fontSize: 12, color: '#92400e', marginTop: 4 }}>
                {tenantArrearsDetail.months_overdue + ' month(s) unpaid · Unit ' +
                  tenantArrearsDetail.unit_number}
              </p>
            </div>

            {tenantArrearsDetail.arrears.map(function(a) {
              const isUpcoming = a.status === 'upcoming';
              const isOverdue = !isUpcoming && a.days_overdue > 30;
              return (
                <div key={a.month} style={{
                  display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px 0', borderBottom: '1px solid #f1f5f9'
                }}>
                  <div>
                    <p style={{ fontWeight: 500, fontSize: 14 }}>{a.month}</p>
                    <p style={{ color: '#94a3b8', fontSize: 12, marginTop: 2 }}>
                      {isUpcoming
                        ? 'Upcoming — due by 10th'
                        : isOverdue
                          ? a.days_overdue + ' days overdue'
                          : 'Due this month'}
                    </p>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <p style={{ fontWeight: 600, color: isUpcoming ? '#1e40af' : '#dc2626' }}>
                      {'₹' + a.amount.toLocaleString()}
                    </p>
                    <span style={{
                      background: isUpcoming ? '#dbeafe' : isOverdue ? '#fee2e2' : '#fef9c3',
                      color: isUpcoming ? '#1e40af' : isOverdue ? '#991b1b' : '#854d0e',
                      padding: '3px 8px', borderRadius: 20,
                      fontSize: 11, fontWeight: 500
                    }}>
                      {isUpcoming ? 'UPCOMING' : isOverdue ? 'OVERDUE' : 'DUE'}
                    </span>
                  </div>
                </div>
              );
            })}

            <button
              onClick={() => setShowTenantArrears(false)}
              style={{
                width: '100%', padding: 11,
                background: '#f1f5f9', border: 'none',
                borderRadius: 8, fontSize: 14,
                cursor: 'pointer', color: '#64748b',
                marginTop: 20
              }}>
              Close
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
